import { useState } from 'react'
import { clsx } from 'clsx'
import type { DraftPlayer } from '../types'
import { getStarterCounts, maxStartersForPosition } from '../squadUtils'
import { InfoButton } from '@/shared/components/ui/InfoButton'
import { PitchPlayerSlot } from './PitchPlayerSlot'
import { SquadRulesSheet } from './SquadRulesSheet'
import styles from './TacticalPitch.module.css'

type TacticalPitchProps = {
  starters:          readonly DraftPlayer[]
  isWindowOpen:      boolean
  subbedOutIds?:     ReadonlySet<string>
  onRemovePlayer?:   (playerId: string) => void
  onClickPlayer?:    (playerId: string) => void
  onFilterPosition?: (position: 'GK' | 'DEF' | 'MID' | 'FWD') => void   // ghost "+" → filter list by band
}

// ── Formation display logic ────────────────────────────────────────────────────
// Empty circles are DYNAMIC. Each outfield band shows as many slots as the MAXIMUM number
// of that position reachable across every still-valid tactic given the other bands' current
// picks (see maxStartersForPosition). So the pitch starts at the absolute max (5 DEF, 5 MID,
// 3 FWD) and circles disappear as picks rule formations out — e.g. picking a 5th DEF caps
// MID at 4. Removing a pick brings the circle back. GK is always a single slot.

type RowSpec = { position: 'GK' | 'DEF' | 'MID' | 'FWD'; count: number }

function buildRows(starters: readonly DraftPlayer[]): RowSpec[] {
  const counts = getStarterCounts(starters)
  return [
    { position: 'GK',  count: 1 },
    { position: 'DEF', count: maxStartersForPosition('DEF', counts) },
    { position: 'MID', count: maxStartersForPosition('MID', counts) },
    { position: 'FWD', count: maxStartersForPosition('FWD', counts) },
  ]
}

// ── Component ──────────────────────────────────────────────────────────────────

export function TacticalPitch({
  starters,
  isWindowOpen,
  subbedOutIds,
  onRemovePlayer,
  onClickPlayer,
  onFilterPosition,
}: TacticalPitchProps) {
  const rows = buildRows(starters)
  const [showRules, setShowRules] = useState(false)

  // Group starters by position, preserving their original index in draft.starters[]
  // so we can pass starterIndex to PitchPlayerSlot for DnD.
  type IndexedPlayer = { player: DraftPlayer; index: number }
  const byPosition: Partial<Record<'GK' | 'DEF' | 'MID' | 'FWD', IndexedPlayer[]>> = {}
  starters.forEach((p, index) => {
    const pos = p.position
    if (!byPosition[pos]) byPosition[pos] = []
    byPosition[pos]!.push({ player: p, index })
  })

  return (
    <div className={styles.pitchWrap}>
      {/* CSS-drawn pitch surface */}
      <div className={styles.pitch} aria-label="Campo de futebol">
        {/* Rules info — top-right corner of the field */}
        <InfoButton
          label="Ver regras de escalação"
          className={styles.infoBtn}
          onClick={() => setShowRules(true)}
        />

        {/* Centre circle */}
        <div className={styles.centreCircle} aria-hidden="true" />
        <div className={styles.halfwayLine}  aria-hidden="true" />
        <div className={styles.penaltyTop}   aria-hidden="true" />
        <div className={styles.penaltyBot}   aria-hidden="true" />

        {/* Formation rows — rendered FWD→MID→DEF→GK (top to bottom) */}
        {[...rows].reverse().map((row) => {
          const filled = byPosition[row.position] ?? []

          return (
            <div
              key={row.position}
              className={clsx(styles.row, styles[`row${row.position}`])}
            >
              {Array.from({ length: row.count }, (_, i) => {
                const entry = filled[i]
                return (
                  <PitchPlayerSlot
                    key={entry?.player.playerId ?? `empty-${row.position}-${i}`}
                    player={entry?.player ?? null}
                    {...(entry !== undefined
                      ? { starterIndex: entry.index }
                      : { emptyPosition: row.position, emptySlotIndex: i })}
                    {...(onFilterPosition !== undefined ? { onFilterPosition } : {})}
                    isWindowOpen={isWindowOpen}
                    isSubbedOut={entry !== undefined && (subbedOutIds?.has(entry.player.playerId) ?? false)}
                    {...(onRemovePlayer !== undefined ? { onRemove: onRemovePlayer } : {})}
                    {...(onClickPlayer !== undefined ? { onClickPlayer } : {})}
                  />
                )
              })}
            </div>
          )
        })}
      </div>

      {showRules && <SquadRulesSheet onClose={() => setShowRules(false)} />}
    </div>
  )
}
